import * as interfaces from '../../interfaces/index.js';

import {
  DeesElement,
  type TemplateResult,
  property,
  state,
  customElement,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { demoFunc } from './dees-appui-tabs.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-feedback/dees-badge/dees-badge.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';

@customElement('dees-appui-tabs')
export class DeesAppuiTabs extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE
  @property({
    type: Array,
  })
  accessor tabs: interfaces.IMenuItem[] = [];

  @property({ type: Object })
  accessor selectedTab: interfaces.IMenuItem | null = null;

  @property({ type: Boolean })
  accessor showTabIndicator: boolean = true;

  @property({ type: String })
  accessor tabStyle: 'horizontal' | 'vertical' = 'horizontal';

  /** Flat navigation rows for a tab list embedded in a sidebar. */
  @property({ type: String, reflect: true })
  accessor appearance: 'default' | 'sidebar' = 'default';

  @property({ type: Boolean })
  accessor autoHide: boolean = false;

  @property({ type: Number })
  accessor autoHideThreshold: number = 0;

  @property({ type: Array })
  accessor actionsLeft: interfaces.ITabAction[] = [];

  @property({ type: Array })
  accessor actionsRight: interfaces.ITabAction[] = [];

  // Scroll state for fade indicators
  @state()
  private accessor canScrollLeft: boolean = false;

  @state()
  private accessor canScrollRight: boolean = false;

  private resizeObserver: ResizeObserver | null = null;
  private renaming = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      @media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition: none !important; animation: none !important; } }

      :host {
        display: block;
        position: relative;
        width: 100%;
        min-width: 0;
        overflow: hidden;
      }

      .tabs-wrapper {
        position: relative;
        min-width: 0;
      }

      .tabs-wrapper.horizontal-wrapper {
        height: var(--dees-appui-row-height);
        background: var(--dees-appui-chrome-bg);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        box-sizing: border-box;
        overflow: hidden;
        display: flex;
        align-items: stretch;
      }

      /* Scroll fade indicators */
      .scroll-fade {
        position: absolute;
        top: 0;
        bottom: 1px;
        width: 20px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 10;
      }

      .scroll-fade-left {
        left: 0;
        background: linear-gradient(to right,
          var(--dees-appui-chrome-bg) 0%,
          transparent 100%);
      }

      .scroll-fade-right {
        right: 0;
        background: linear-gradient(to left,
          var(--dees-appui-chrome-bg) 0%,
          transparent 100%);
      }

      .scroll-fade.visible {
        opacity: 1;
      }

      .scroll-area {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        display: flex;
      }

      /* Tab action buttons */
      .tab-actions {
        display: flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        padding: 0 4px;
      }

      .tab-actions.left {
        padding-left: 12px;
        padding-right: 8px;
      }

      .tab-actions.right {
        padding-right: 12px;
        padding-left: 8px;
      }

      .tab-action-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
        background: transparent;
        color: var(--dees-color-text-muted);
        flex-shrink: 0;
      }

      .tab-action-button:hover {
        background: var(--dees-color-active);
        color: var(--dees-color-text-primary);
      }

      .tab-action-button:active {
        background: var(--dees-color-pressed);
      }

      .tab-action-button.disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      .tab-action-button.disabled:hover {
        background: transparent;
        color: var(--dees-color-text-muted);
      }

      .tab-action-button dees-icon {
        font-size: 16px;
      }

      .tabsContainer {
        position: relative;
        user-select: none;
        min-width: 0;
      }

      .tabsContainer.horizontal {
        display: flex;
        align-items: center;
        font-size: var(--dees-tabs-font-size, var(--dees-font-control-size));
        overflow-x: auto;
        overflow-y: hidden;
        overscroll-behavior: contain;
        scrollbar-width: thin;
        scrollbar-color: transparent transparent;
        height: 100%;
        width: 100%;
        padding: 4px 12px 0;
        gap: 4px;
      }

      /* Show scrollbar on hover */
      .tabs-wrapper:hover .tabsContainer.horizontal,
      .scroll-area:hover .tabsContainer.horizontal {
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      .tabsContainer.horizontal::-webkit-scrollbar {
        height: 4px;
      }

      .tabsContainer.horizontal::-webkit-scrollbar-track {
        background: transparent;
      }

      .tabsContainer.horizontal::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 2px;
        transition: background 0.2s ease;
      }

      .tabs-wrapper:hover .tabsContainer.horizontal::-webkit-scrollbar-thumb,
      .scroll-area:hover .tabsContainer.horizontal::-webkit-scrollbar-thumb {
        background: var(--dees-color-scrollbar-thumb);
      }

      .tabs-wrapper:hover .tabsContainer.horizontal::-webkit-scrollbar-thumb:hover,
      .scroll-area:hover .tabsContainer.horizontal::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-scrollbar-thumb-hover);
      }

      .tabsContainer.vertical {
        display: flex;
        flex-direction: column;
        padding: 8px;
        font-size: var(--dees-tabs-font-size, 14px);
        gap: 2px;
        position: relative;
        background: var(--dees-tabs-vertical-bg, var(--dees-color-fill-secondary));
        border-radius: var(--dees-radius-md);
      }

      .tab {
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
        cursor: pointer;
        transition: color 0.15s ease;
        font-weight: 500;
        position: relative;
        z-index: 2;
      }

      .horizontal .tab {
        padding: 0 12px;
        height: 28px;
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        position: relative;
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        transition: background-color 0.15s ease;
      }
      

      
      .horizontal .tab .tab-content {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .vertical .tab {
        padding: 10px 16px;
        border-radius: 6px;
        width: 100%;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.15s ease;
      }

      .tab:hover {
        color: var(--dees-color-text-primary);
      }
      
      .horizontal .tab:hover {
        background: var(--dees-color-hover);
      }
      
      .horizontal .tab:hover::after,
      .horizontal .tab:hover + .tab::after {
        opacity: 0;
      }

      .vertical .tab:hover {
        background: color-mix(in srgb, var(--dees-color-badge-default-bg) 50%, transparent);
      }

      .horizontal .tab.selectedTab {
        color: var(--dees-color-text-primary);
      }
      
      .horizontal .tab.selectedTab::after,
      .horizontal .tab.selectedTab + .tab::after {
        opacity: 0;
      }

      .vertical .tab.selectedTab {
        color: var(--dees-color-text-primary);
      }

      .tab dees-icon {
        font-size: 16px;
      }

      .vertical-wrapper {
        position: relative;
      }

      /* The selection surface belongs to its tab, including while scrolling,
         resizing or loading fonts; no measured overlay or layout timers. */
      .tab.has-indicator, .tab.has-indicator:hover {
        background: var(--dees-appui-tab-selected-bg);
        box-shadow: var(--dees-shadow-xs);
        color: var(--dees-color-text-primary);
      }

      button { font: inherit; border: 0; background: transparent; color: inherit; cursor: pointer; }
      button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      .tab-select { display: flex; align-items: center; gap: 8px; align-self: stretch; padding: 0; flex: 1; text-align: left; }
      .tabsContainer { font: 13px var(--dees-font-family); }
      .tab-content { display: flex; align-items: center; gap: 8px; }
      .tab-select, .tab-content, .tab-label { min-width: 0; }
      .tab-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .tab-content dees-icon, .tab-content dees-badge { flex-shrink: 0; }
      :host([appearance='sidebar']) .tabsContainer.vertical { padding: 4px 6px; gap: 2px; background: transparent; border-radius: 0; }
      :host([appearance='sidebar']) .vertical .tab { min-height: 30px; padding: 0 8px; gap: 4px; border-radius: var(--dees-radius-sm); }
      :host([appearance='sidebar']) .vertical .tab-close { width: 22px; height: 22px; margin-left: 0; }
      :host([appearance='sidebar']) .vertical .tab.has-indicator { background: var(--dees-color-sidebar-selection); color: var(--dees-color-text-primary); box-shadow: none; }
      :host([appearance='sidebar']) .vertical .tab dees-icon { font-size: 14px; }
      @media (pointer: coarse) {
        .tab-close, .tab-action-button { min-width: 44px; min-height: 44px; }
        .tab-select { min-height: 44px; }
        .horizontal .tab { height: 44px; }
        .tabsContainer.horizontal { padding-block: 0; }
      }
      /* Close button */
      .tab-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 4px;
        margin-left: 8px;
        transition: color 0.15s, background 0.15s;
        color: var(--dees-color-text-muted);
      }

      .tab:hover .tab-close {
        color: var(--dees-color-text-secondary);
      }

      .tab-close:hover {
        background: var(--dees-color-pressed);
        color: var(--dees-color-accent-error);
      }

      .tab.selectedTab .tab-close {
        color: var(--dees-color-text-secondary);
      }

      .tab.selectedTab:hover .tab-close {
        color: var(--dees-color-text-primary);
      }

      .tab.selectedTab .tab-close:hover {
        opacity: 1;
      }
    `,
  ];

  public render(): TemplateResult {
    // Auto-hide when enabled and tab count is at or below threshold
    if (this.autoHide && this.tabs.length <= this.autoHideThreshold) {
      return html``;
    }
    return html`
      ${this.renderTabsWrapper()}
    `;
  }

  private renderTabsWrapper(): TemplateResult {
    const isHorizontal = this.tabStyle === 'horizontal';
    const wrapperClass = isHorizontal ? 'tabs-wrapper horizontal-wrapper' : 'vertical-wrapper';
    const containerClass = `tabsContainer ${this.tabStyle}`;

    if (isHorizontal) {
      const hasLeftActions = this.actionsLeft && this.actionsLeft.length > 0;
      const hasRightActions = this.actionsRight && this.actionsRight.length > 0;

      return html`
        <div class="${wrapperClass}">
          ${hasLeftActions ? this.renderActions(this.actionsLeft, 'left') : ''}
          <div class="scroll-area">
            <div class="scroll-fade scroll-fade-left ${this.canScrollLeft ? 'visible' : ''}"></div>
            <div role="tablist" aria-label="Views" aria-orientation=${this.tabStyle} class="${containerClass}" @scroll=${this.handleScroll}>
              ${this.tabs.map(tab => this.renderTab(tab, isHorizontal))}
            </div>
            <div class="scroll-fade scroll-fade-right ${this.canScrollRight ? 'visible' : ''}"></div>
          </div>
          ${hasRightActions ? this.renderActions(this.actionsRight, 'right') : ''}
        </div>
      `;
    }

    return html`
      <div class="${wrapperClass}">
        <div role="tablist" aria-label="Views" aria-orientation=${this.tabStyle} class="${containerClass}">
          ${this.tabs.map(tab => this.renderTab(tab, isHorizontal))}
        </div>
      </div>
    `;
  }

  private renderActions(actions: interfaces.ITabAction[], position: 'left' | 'right'): TemplateResult {
    return html`<div class="tab-actions ${position}">${actions.map(action => html`
      <button type="button" class="tab-action-button ${action.disabled ? 'disabled' : ''}" ?disabled=${action.disabled}
        aria-label=${action.tooltip || action.id} title=${action.tooltip || action.id} @click=${() => action.action()}>
        <dees-icon .icon=${action.iconName}></dees-icon>
      </button>`)}</div>`;
  }

  private renderTab(tab: interfaces.IMenuItem, _isHorizontal: boolean): TemplateResult {
    const isSelected = this.isTabSelected(tab);
    const label = tab.label ?? tab.key;
    return html`<div class="tab ${isSelected ? 'selectedTab' : ''} ${isSelected && this.showTabIndicator ? 'has-indicator' : ''}" role="presentation">
      <button type="button" class="tab-select" role="tab" title=${label} aria-selected=${isSelected} tabindex=${isSelected ? 0 : -1}
        @dblclick=${() => this.renameTab(tab)} @contextmenu=${(event: MouseEvent) => this.openRenameMenu(event, tab)}
        @keydown=${(event: KeyboardEvent) => this.handleTabKey(event, tab)} @click=${() => this.selectTab(tab)}>
        <span class="tab-content">${this.renderTabIcon(tab)}<span class="tab-label">${label}</span>${tab.badge !== undefined ? html`<dees-badge .text=${String(tab.badge)} .type=${tab.badgeVariant || 'default'}></dees-badge>` : ''}</span>
      </button>
      ${tab.closeable ? html`<button type="button" class="tab-close" aria-label=${`Close ${label}`} @click=${(event: Event) => this.closeTab(event, tab)}>
        <dees-icon icon="lucide:X" .iconSize=${12}></dees-icon></button>` : ''}
    </div>`;
  }

  private handleTabKey(event: KeyboardEvent, tab: interfaces.IMenuItem) {
    if (event.key === 'F2' && tab.onRename) { event.preventDefault(); void this.renameTab(tab); return; }
    if (tab.onRename && (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))) {
      event.preventDefault();
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      void this.openRenameMenu(new MouseEvent('contextmenu', { clientX: rect.left, clientY: rect.bottom }), tab);
      return;
    }
    const previous = this.tabStyle === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    const next = this.tabStyle === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    if (event.key === 'Delete' && tab.closeable) { event.preventDefault(); this.closeTab(event, tab); return; }
    if (![previous, next, 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = this.tabs.indexOf(tab);
    const target = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === next ? 1 : -1) + this.tabs.length) % this.tabs.length;
    this.shadowRoot!.querySelectorAll<HTMLButtonElement>('.tab-select')[target]?.focus();
  }

  private async renameTab(tab: interfaces.IMenuItem) {
    if (!tab.onRename || this.renaming) return;
    this.renaming = true;
    try { await tab.onRename(); } finally { this.renaming = false; }
    await this.updateComplete;
    const index = this.tabs.findIndex(item => item.key === tab.key);
    this.shadowRoot?.querySelectorAll<HTMLButtonElement>('.tab-select')[index]?.focus();
  }

  private async openRenameMenu(event: MouseEvent, tab: interfaces.IMenuItem) {
    if (!tab.onRename) return;
    event.preventDefault(); event.stopPropagation();
    await DeesContextmenu.openContextMenuWithOptions(event, [{
      name: 'Rename', iconName: 'lucide:pencil', action: async () => { await this.renameTab(tab); },
    }]);
  }

  private renderTabIcon(tab: interfaces.IMenuItem): TemplateResult | '' {
    return tab.iconName ? html`<dees-icon .icon=${tab.iconName}></dees-icon>` : '';
  }

  /**
   * The stable identity of a tab descriptor. `key` is the documented
   * identifier; `label` is the fallback for descriptors that carry only a
   * display name.
   */
  private tabKey(tabArg: interfaces.IMenuItem | null | undefined): string | undefined {
    return tabArg ? (tabArg.key ?? tabArg.label) : undefined;
  }

  /** The current descriptor for a selection, matched by key rather than by object identity. */
  private findTabByKey(tabArg: interfaces.IMenuItem | null): interfaces.IMenuItem | undefined {
    const key = this.tabKey(tabArg);
    if (key === undefined) return this.tabs.find((tab) => tab === tabArg);
    return this.tabs.find((tab) => this.tabKey(tab) === key);
  }

  private isTabSelected(tabArg: interfaces.IMenuItem): boolean {
    if (!this.selectedTab) return false;
    if (tabArg === this.selectedTab) return true;
    const key = this.tabKey(tabArg);
    return key !== undefined && key === this.tabKey(this.selectedTab);
  }

  private selectTab(tabArg: interfaces.IMenuItem) {
    this.selectedTab = tabArg;
    tabArg.action();

    // Scroll selected tab into view
    requestAnimationFrame(() => {
      this.scrollTabIntoView(tabArg);
    });

    // Emit tab-select event
    this.dispatchEvent(new CustomEvent('tab-select', {
      detail: { tab: tabArg },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Clear all tabs and reset selection.
   */
  public clear(): void {
    this.tabs = [];
    this.selectedTab = null;
  }

  private async closeTab(e: Event, tab: interfaces.IMenuItem) {
    const index = this.tabs.indexOf(tab);
    e.stopPropagation();  // Don't select tab when closing

    // Call the tab's onClose callback if defined
    if (tab.onClose) {
      tab.onClose();
    }

    // Also emit event for parent components
    this.dispatchEvent(new CustomEvent('tab-close', {
      detail: { tab },
      bubbles: true,
      composed: true
    }));
    await this.updateComplete;
    const buttons = this.shadowRoot!.querySelectorAll<HTMLButtonElement>('.tab-select');
    buttons[Math.min(index, buttons.length - 1)]?.focus();
  }

  firstUpdated() {
    // Tab selection is handled by updated() lifecycle
    this.setupResizeObserver();

    requestAnimationFrame(() => {
      this.updateScrollState();
    });
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  private setupResizeObserver() {
    if (this.tabStyle !== 'horizontal') return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollState();
    });

    const container = this.shadowRoot?.querySelector('.tabsContainer.horizontal');
    if (container) {
      this.resizeObserver.observe(container);
    }
  }

  private handleScroll = () => {
    this.updateScrollState();
  };

  private updateScrollState() {
    const container = this.shadowRoot?.querySelector('.tabsContainer.horizontal') as HTMLElement;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // Small threshold to account for rounding
    const threshold = 2;

    this.canScrollLeft = scrollLeft > threshold;
    this.canScrollRight = scrollLeft < scrollWidth - clientWidth - threshold;
  }

  private scrollTabIntoView(tab: interfaces.IMenuItem) {
    if (this.tabStyle !== 'horizontal') return;

    const tabIndex = this.tabs.indexOf(tab);
    if (tabIndex === -1) return;

    const container = this.shadowRoot?.querySelector('.tabsContainer.horizontal') as HTMLElement;
    const tabElement = container?.querySelector(`.tab:nth-child(${tabIndex + 1})`) as HTMLElement;

    if (tabElement && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = tabElement.getBoundingClientRect();

      // Check if tab is fully visible
      const isFullyVisible =
        tabRect.left >= containerRect.left &&
        tabRect.right <= containerRect.right;

      if (!isFullyVisible) {
        tabElement.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }
  }

  /**
   * A host that binds a literal `.tabs=${[...]}` hands us new descriptor
   * objects on every render. The selection follows the descriptor key, so the
   * open view survives a rerender; adopting the fresh object here keeps index
   * lookups (close, scroll-into-view) working without re-running the action.
   */
  public willUpdate(changedProperties: Map<string, any>) {
    super.willUpdate(changedProperties);
    if (!changedProperties.has('tabs') || !this.selectedTab) return;
    const current = this.findTabByKey(this.selectedTab);
    if (current && current !== this.selectedTab) this.selectedTab = current;
  }

  async updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('tabs')) {
      if (!this.tabs || this.tabs.length === 0) {
        // Tabs are empty => reset selection
        if (this.selectedTab !== null) {
          this.selectedTab = null;
          this.dispatchEvent(new CustomEvent('tab-select', {
            detail: { tab: null },
            bubbles: true,
            composed: true,
          }));
        }
      } else if (this.selectedTab && !this.findTabByKey(this.selectedTab)) {
        // Selected tab was removed => select first available
        this.selectTab(this.tabs[0]);
      } else if (!this.selectedTab) {
        // Tabs exist but nothing selected => select first
        this.selectTab(this.tabs[0]);
      }
    }

    if (changedProperties.has('selectedTab') || changedProperties.has('tabs')) {
      await this.updateComplete;
      this.updateScrollState();
    }
  }
}
